import { useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { cx, fmtPct } from './format';
import { Icon } from './Icon';
import { toast } from './Toast';
import s from './ui.module.css';

export type Tone = 'neutral' | 'ok' | 'warn' | 'danger' | 'info' | 'accent';

/** A section title on a black brush-stroke plate (the website's section titles). */
export function Plate({ children, large = false }: { children: ReactNode; large?: boolean }) {
  return <span className={cx(s.plate, large && s.plateLg)}>{children}</span>;
}

export function Card({ title, sub, actions, flush = false, children, className }: { title?: ReactNode; sub?: ReactNode; actions?: ReactNode; flush?: boolean; children: ReactNode; className?: string }) {
  const head = !!(title || actions);
  return (
    <section className={cx(s.card, className)}>
      {head && (
        <header className={s.cardHead}>
          <h2>
            {title && <Plate>{title}</Plate>}
            {sub && <span className={s.cardSub}>{sub}</span>}
          </h2>
          {actions && <div className="row">{actions}</div>}
        </header>
      )}
      <div className={cx(s.cardBody, flush && s.flush, !head && s.cardHeadless)}>{children}</div>
    </section>
  );
}

export function StatGrid({ children }: { children: ReactNode }) {
  return <div className={s.stats}>{children}</div>;
}

/** Change against the previous period: an arrow and a percentage; `invert` when down is good. */
export function Delta({ now, before, invert = false }: { now: number; before: number | null | undefined; invert?: boolean }) {
  if (before == null) return null;
  if (before === 0) return now === 0 ? <span className={s.delta} data-dir="flat">— 與上期相同</span> : <span className={s.delta} data-dir={invert ? 'down' : 'up'}>新增 · 上期 0</span>;
  const pct = ((now - before) / before) * 100;
  const dir = Math.abs(pct) < 0.5 ? 'flat' : (pct > 0) !== invert ? 'up' : 'down';
  return (
    <span className={s.delta} data-dir={dir} title={`上期 ${before.toLocaleString('en-US')}`}>
      {dir !== 'flat' && <Icon name={pct > 0 ? 'up' : 'down'} size={14} strokeWidth={2.4} />}
      {pct > 0 ? '+' : ''}
      {Math.round(pct)}% vs 上期
    </span>
  );
}

/** A KPI tile; with `to` it links to the page that explains the number. */
export function Stat({ label, value, hint, tone, to, delta }: { label: string; value: ReactNode; hint?: ReactNode; tone?: Tone | undefined; to?: string; delta?: ReactNode }) {
  const body = (
    <>
      <span className={s.statLabel}>{label}</span>
      <span className={s.statValue}>{value}</span>
      {delta}
      {hint && <span className={s.statHint}>{hint}</span>}
    </>
  );
  return to ? (
    <Link to={to} className={s.stat} data-tone={tone}>
      {body}
    </Link>
  ) : (
    <div className={s.stat} data-tone={tone}>
      {body}
    </div>
  );
}

export function Badge({ tone = 'neutral', children, title }: { tone?: Tone; children: ReactNode; title?: string }) {
  return (
    <span className={s.badge} data-tone={tone} title={title}>
      {children}
    </span>
  );
}

export function Count({ n }: { n: number }) {
  return n > 0 ? <span className={s.count}>{n > 99 ? '99+' : n}</span> : null;
}

export function KeyValues({ items }: { items: Array<[string, ReactNode]> }) {
  return (
    <dl className={s.kv}>
      {items.map(([k, v]) => (
        <div key={k} style={{ display: 'contents' }}>
          <dt>{k}</dt>
          <dd>{v}</dd>
        </div>
      ))}
    </dl>
  );
}

export function Section({ title, children, actions }: { title: string; children: ReactNode; actions?: ReactNode }) {
  return (
    <section className={s.section}>
      <div className="spread">
        <h3 className={s.sectionTitle}>{title}</h3>
        {actions}
      </div>
      {children}
    </section>
  );
}

export function JsonView({ value }: { value: unknown }) {
  const text = value === undefined ? 'undefined' : JSON.stringify(value, null, 2);
  return <pre className={s.json}>{text}</pre>;
}

export function copy(text: string, what = 'ID') {
  void navigator.clipboard
    ?.writeText(text)
    .then(() => toast(`已複製${what}`))
    .catch(() => undefined);
}

/** A monospace id, shortened (full value on hover), with a copy button. */
export function Id({ value, full = false }: { value: string | null | undefined; full?: boolean }) {
  if (!value) return <span className="faint">—</span>;
  return (
    <span className={s.id} title={value}>
      <code>{full || value.length <= 10 ? value : `${value.slice(0, 8)}…`}</code>
      <button
        type="button"
        className={s.iconBtn}
        aria-label="複製"
        onClick={(e) => {
          e.stopPropagation();
          copy(value);
        }}
      >
        <Icon name="copy" size={14} />
      </button>
    </span>
  );
}

/** A round portrait, or the first character of the name. */
export function Avatar({ url, name, size = 40 }: { url?: string | null | undefined; name: string; size?: number }) {
  const [broken, setBroken] = useState(false);
  return (
    <span className={s.avatar} style={{ width: size, height: size, fontSize: size * 0.42 }}>
      {url && !broken ? <img src={url} alt="" width={size} height={size} style={{ objectFit: 'cover', objectPosition: 'top' }} onError={() => setBroken(true)} /> : name.slice(0, 1)}
    </span>
  );
}

/** A 0–100 bar with its number (relationship dimensions). */
export function ScoreBar({ value, color = 'var(--chart-1)' }: { value: number; color?: string }) {
  const v = Math.max(0, Math.min(100, value));
  return (
    <div className={s.bar} title={fmtPct(v, 100)}>
      <span style={{ width: `${v}%`, background: color }} />
    </div>
  );
}
